import { useState } from "react";
import { AttemptResult, QuestionPublic, api } from "../lib/api";

interface Props {
  question: QuestionPublic;
  mode: "practice" | "daily_review" | "mock_exam";
  onNext: () => void;
  onAnswered?: (result: AttemptResult) => void;
}

type Rating = 1 | 2 | 3 | 4;

// FSRS self-assessment, shown only in Daily Review. Outline style keeps the
// restrained palette; the explicit rating drives card scheduling server-side.
const RATINGS: { value: Rating; label: string; classes: string }[] = [
  { value: 1, label: "À revoir", classes: "border-red-600 text-red-700 hover:bg-red-600/10" },
  {
    value: 2,
    label: "Difficile",
    classes: "border-scribe-500 text-scribe-600 hover:bg-scribe-500/10",
  },
  { value: 3, label: "Correct", classes: "border-green-600 text-green-700 hover:bg-green-600/10" },
  { value: 4, label: "Facile", classes: "border-lapis-500 text-lapis-600 hover:bg-lapis-500/10" },
];

export default function QuestionCard({ question, mode, onNext, onAnswered }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [revealed, setRevealed] = useState<{
    correct_option_ids: string[];
    explanation: string;
    option_explanations?: Record<string, string>;
    sources: string[];
  } | null>(null);
  const [startedAt] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const isMulti = question.type === "multi_choice";
  const isReview = mode === "daily_review";
  const isExam = mode === "mock_exam";

  const toggle = (id: string) => {
    if (revealed) return;
    setSelected((s) => {
      const next = new Set(s);
      if (isMulti) {
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
      } else {
        next.clear();
        next.add(id);
      }
      return next;
    });
  };

  const elapsed = () => (Date.now() - startedAt) / 1000;

  // Practice: record then reveal. Exam: record then straight to next, no
  // feedback. Review: reveal first, recording is deferred to the rating step.
  const submit = async () => {
    setBusy(true);
    try {
      if (isReview) {
        const r = await api.revealAnswer(question.id);
        setRevealed(r);
        return;
      }
      const result = await api.recordAttempt({
        question_id: question.id,
        selected_option_ids: Array.from(selected),
        mode,
        time_taken_seconds: elapsed(),
      });
      onAnswered?.(result);
      if (isExam) {
        onNext();
        return;
      }
      const r = await api.revealAnswer(question.id);
      setRevealed(r);
    } finally {
      setBusy(false);
    }
  };

  const rateAndNext = async (rating: Rating) => {
    setBusy(true);
    try {
      const result = await api.recordAttempt({
        question_id: question.id,
        selected_option_ids: Array.from(selected),
        mode,
        time_taken_seconds: elapsed(),
        rating,
      });
      onAnswered?.(result);
      onNext();
    } finally {
      setBusy(false);
    }
  };

  const colorFor = (id: string) => {
    if (!revealed)
      return selected.has(id) ? "border-lapis-500 bg-lapis-500/10" : "border-papyrus-200";
    const isCorrect = revealed.correct_option_ids.includes(id);
    const wasSelected = selected.has(id);
    if (isCorrect) return "border-green-600 bg-green-600/10";
    if (wasSelected && !isCorrect) return "border-red-600 bg-red-600/10";
    return "border-papyrus-200 opacity-60";
  };

  return (
    <div className="card max-w-2xl mx-auto">
      <div className="flex items-center gap-2 text-xs text-scribe-600 mb-3 font-mono uppercase tracking-wider">
        <span>{question.domain}</span>
        <span>·</span>
        <span>Niveau {question.difficulty}</span>
      </div>

      <h2 className="font-display text-xl mb-6 leading-relaxed">{question.stem}</h2>

      <div className="space-y-2">
        {question.options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => toggle(opt.id)}
            className={`w-full text-left p-3 border rounded transition-colors ${colorFor(opt.id)}`}
            disabled={!!revealed}
          >
            <span className="font-mono text-sm text-scribe-600 mr-3">{opt.id}.</span>
            {opt.text}
            {revealed?.option_explanations?.[opt.id] && (
              <span className="block mt-1.5 pl-7 text-xs text-scribe-600 leading-relaxed">
                {revealed.option_explanations[opt.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {revealed && (
        <div className="mt-6 p-4 bg-papyrus-50 border-l-4 border-lapis-500 rounded">
          <p className="text-sm leading-relaxed">{revealed.explanation}</p>
          {revealed.sources.length > 0 && (
            <ul className="mt-2 text-xs text-scribe-600">
              {revealed.sources.map((s) => (
                <li key={s}>
                  <a href={s} target="_blank" rel="noreferrer" className="hover:underline">
                    {s}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-6">
        {!revealed ? (
          <div className="flex justify-end">
            <button onClick={submit} disabled={selected.size === 0 || busy} className="btn-primary">
              {isReview ? "Afficher la réponse" : "Valider"}
            </button>
          </div>
        ) : isReview ? (
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-scribe-600 mb-2">
              Ton ressenti
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {RATINGS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => rateAndNext(r.value)}
                  disabled={busy}
                  className={`btn border py-3 ${r.classes}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <button onClick={onNext} className="btn-accent">
              Question suivante
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
